import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema, productSchema } from '../components/StructuredData';
import './Pricing.css';

const Pricing: React.FC = () => {
  return (
    <div className="pricing-page">
      <SEO 
        title="Pricing - TableChat | Simple Transparent Pricing"
        description="Simple transparent pricing for TableChat restaurant communication system. $379/month includes everything you need."
        canonical="https://tablechat.io/pricing"
      />
      <StructuredData data={productSchema('TableChat Complete', 379, 'NFC-powered guest engagement platform for restaurants. Includes unlimited staff accounts, real-time messaging, automated promotions, VIP loyalty capture, marketing integrations, daily reports, and custom branding. Annual commitment.')} />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Pricing', url: 'https://tablechat.io/pricing' },
      ])} />
      <div className="container">
        <div className="pricing-header">
          <h1>Simple Transparent Pricing</h1>
          <p>Built for Restaurant Economics.</p>
        </div>

        <div className="pricing-content">
          {/* Main Pricing Card */}
          <div className="pricing-card main-card">
            <div className="card-header">
              <h2>TableChat Complete</h2>
              <p>Everything you need for exceptional customer service</p>
            </div>

            <div className="pricing-details">
              <div className="price-section">
                <div className="price-item-centered">
                  <div className="price-info">
                    <div className="price-label">Complete Solution</div>
                    <div className="price-value">
                      <span className="currency">$</span>
                      <span className="amount">379</span>
                      <span className="period">/month</span>
                    </div>
                    <div className="price-description">Everything you need • Annual commitment</div>
                  </div>
                </div>
              </div>

            </div>

            <div className="features-included">
              <h4>What's Included</h4>
              <ul className="features-list">
                <li>
                  <svg className="check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Unlimited staff accounts per restaurant
                </li>
                <li>
                  <svg className="check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Instant NFC-powered customer messaging
                </li>
                <li>
                  <svg className="check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Real-time message routing & notifications
                </li>
                <li>
                  <svg className="check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Custom branding & restaurant logo
                </li>
                <li>
                  <svg className="check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Guest to management feedback
                </li>
                <li>
                  <svg className="check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Email support & setup assistance
                </li>
                <li>
                  <svg className="check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Annual contract • Flexible terms
                </li>
              </ul>
            </div>

            <div className="card-actions">
              <Link to="/signup" className="btn btn-primary btn-large">
                Get Started Today
              </Link>
            </div>
          </div>


          {/* Benefits Section */}
          <div className="pricing-benefits">
            <h3>Why TableChat Pays for Itself</h3>
            
            <div className="benefit-cards">
              <div className="benefit-card">
                <h4><span className="benefit-icon">👥</span>Guest Experience</h4>
                <h5>Guests Never Have to Wait</h5>
                <p>Give guests a simple way to ask for refills, extras, or the check the moment they need it—without waiting for a server to return to the table.</p>
              </div>
              
              <div className="benefit-card">
                <h4><span className="benefit-icon">💪</span>Staff Enablement</h4>
                <h5>Support Your Team at Peak Times</h5>
                <p>TableChat helps servers manage more tables smoothly by organizing guest requests in one place—so nothing gets missed, even during the rush.</p>
              </div>
              
              <div className="benefit-card">
                <h4><span className="benefit-icon">✨</span>Operational Value</h4>
                <h5>Better Flow. Better Outcomes.</h5>
                <p>Faster responses, fewer interruptions, and clearer communication lead to happier guests, smoother shifts, and more consistent service across every table.</p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="pricing-faq">
          <h3>Common Pricing Questions</h3>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h4>What's included in the monthly price?</h4>
              <p>Everything you need! Your flat monthly rate includes unlimited staff accounts, all software features, support, and updates.</p>
            </div>
            
            <div className="faq-item">
              <h4>What are the contract terms?</h4>
              <p>TableChat subscriptions are on an annual contract basis. This ensures we can provide consistent service and support to help your restaurant succeed.</p>
            </div>
            
          </div>
        </div>

        {/* CTA Section */}
        <div className="pricing-cta">
          <div className="cta-content">
            <h3>Ready to Transform Your Customer Service?</h3>
            <p>Join restaurants already using TableChat to deliver exceptional dining experiences.</p>
            <Link to="/signup" className="btn btn-primary btn-large">
              Start Your Setup Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
